import { call, get, sync } from 'vuex-pathify';
import { sitesTypes, documentation } from '@/assets/js/rziData';
import { GeoJSON } from 'ol/format';
import { MultiPoint, MultiPolygon } from 'ol/geom';
import { prepareDrawnGeometry } from '@/assets/js/mapUtils';
import Feature from 'ol/src/Feature';

const imagePlaceholder = require('@/assets/images/image_placeholder.png');

const drawing = {
  data: () => ({
    drawingGeometry: null,
    editingMode: null /*** add | edit | part | delete */,
  }),
  computed: {
    projectElements: get('layers/project@layers'),
    tableHeight: sync('table/tableHeight'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    computedStickyButtons: {
      get() {
        return this.stickyButtons;
      },
      set(nV) {
        this.$emit('update:stickyButtons', nV);
      },
    },
    computedIsDrawingOn: {
      get() {
        return this.isDrawingOn;
      },
      set(nV) {
        this.$emit('update:isDrawingOn', nV);
      },
    },
  },
  methods: {
    handleFeatureHover(geometry) {
      this.$root.$emit('toggleCustomVectorLayer', {
        id: 'protected-site-table',
        ...(geometry && { geometry }),
      });
    },
    zoomToFeature(geometry) {
      if (this.tableHeight === 3) this.tableHeight = 2;
      setTimeout(() => {
        this.$root.$emit('createGeomAndFitView', {
          ...geometry,
          crs: {
            type: 'name',
            properties: {
              name: this.$_config.defaultEpsg,
            },
          },
        });
      }, 0);
    },
    handleCancelGeometry() {
      this.$root.$emit('deleteSidebarGeometry');
      this.computedIsDrawingOn = false;
      this.drawingGeometry = null;
    },
    setStickyButtonProperties(data) {
      let copiedButtons = [...this.stickyButtons];
      Object.entries(data).forEach(([buttonName, properties]) => {
        const foundButtonIndex = copiedButtons.findIndex(button => button.value === buttonName);
        if (foundButtonIndex < 0) return;
        copiedButtons[foundButtonIndex] = {
          ...copiedButtons[foundButtonIndex],
          ...properties,
        };
      });
      this.computedStickyButtons = copiedButtons;
    },
    setDrawingGeometry(geom) {
      this.drawingGeometry = geom;
      this.setStickyButtonProperties({
        deleteMultipart: {
          disabled: geom.coordinates.length <= 1,
        },
      });
    },
    handleModifyGeometry(type, geom = this.drawingGeometry) {
      this.setStickyButtonProperties({
        add: {
          disabled: !geom || geom.coordinates.length < 1,
        },
        deleteMultipart: {
          active: false,
          disabled: geom.coordinates.length <= 1,
        },
        addMultipart: {
          active: false,
          disabled: !geom,
        },
      });
      this.$forceUpdate();
      this.editingMode = 'edit';
      this.$root.$emit('modifySidebarGeometry', {
        geometryValue: geom,
        modifyendCallback: eModify => {
          this.drawingGeometry = prepareDrawnGeometry(eModify.features.getArray()[0]);
        },
      });
    },
    handleAddMultipart(type) {
      this.$root.$emit('toggleAddMultipartGeometry', {
        type,
        geometryValue: this.drawingGeometry,
        params: {
          geometryType: type,
          callback: e => {
            const wholeGeometry = new GeoJSON().readGeometry(this.drawingGeometry, {
              dataProjection: 'EPSG:3857',
              featureProjection: 'EPSG:3857',
            });
            wholeGeometry[`append${this.getOlGeometrySimpleType(this.drawingGeometry.type)}`](e.feature.getGeometry());
            const drawnGeometry = prepareDrawnGeometry(new Feature({ geometry: wholeGeometry }));
            this.setDrawingGeometry(drawnGeometry);
            this.handleAddMultipart(type);
          },
        },
      });
    },
    toggleAddMultipart(type) {
      this.setStickyButtonProperties({
        deleteMultipart: {
          active: false,
        },
        addMultipart: {
          active: true,
        },
      });
      if (this.editingMode === 'part') {
        this.handleModifyGeometry(type);
        return;
      }
      this.editingMode = 'part';
      this.handleAddMultipart(type);
    },
    handleDeleteMultipart(type) {
      this.$root.$emit('toggleRemoveMultipartGeometry', {
        geometryValue: this.drawingGeometry,
        selectCallback: ({ newGeometry }) => {
          this.setDrawingGeometry(prepareDrawnGeometry(newGeometry));
          this.drawingGeometry.coordinates.length <= 1
            ? this.handleModifyGeometry(type)
            : this.handleDeleteMultipart(type);
        },
      });
    },
    toggleDeleteMultipart(type) {
      this.setStickyButtonProperties({
        deleteMultipart: {
          active: true,
        },
        addMultipart: {
          active: false,
        },
      });
      if (this.editingMode === 'delete') {
        this.handleModifyGeometry(type);
        return;
      }
      this.editingMode = 'delete';
      this.handleDeleteMultipart(type);
    },
    refreshLayers(dataArray, callback) {
      this.$root.$emit(
        'refreshMvtSources',
        this.projectLayers
          .filter(layer => dataArray.map(dt => dt.name).includes(layer.data_source_name))
          .map(layer => ({ id: layer.id, group: 'layers' })),
        true
      );
      this.$emit('refreshTableDatasources', dataArray, callback);
    },
    handleAssignGeometry(type, item, addingCallback) {
      if (this.tableHeight === 3) this.tableHeight = 2;
      setTimeout(() => {
        this.computedIsDrawingOn = true;
        this.computedStickyButtons = [
          ...(this.defaultDatasource
            ? [
                {
                  straightText: this.$i18n.t('table.rzi.addParts'),
                  value: 'addMultipart',
                  active: false,
                  icon: 'mdi-plus',
                  disabled: true,
                  tooltip: this.$i18n.t(`table.rzi.add_${type}s`),
                  action: () => this.toggleAddMultipart(type),
                },
                {
                  straightText: this.$i18n.t('table.rzi.removeParts'),
                  value: 'deleteMultipart',
                  active: false,
                  icon: 'mdi-delete',
                  tooltip: this.$i18n.t(`table.rzi.remove_${type}s`),
                  action: () => this.toggleDeleteMultipart(type),
                  disabled: true,
                },
              ]
            : []),
          {
            straightText: this.$i18n.t('button.save'),
            value: 'add',
            action: () => {
              if (!item) {
                if (addingCallback) addingCallback();
                return;
              }
              this.setStickyButtonProperties({
                add: {
                  loading: true,
                },
                cancel: {
                  disabled: true,
                },
              });
              if (addingCallback) addingCallback();
            },
            disabled: true,
          },
          {
            straightText: this.$i18n.t('button.cancel'),
            value: 'cancel',
            action: this.handleCancelGeometry,
            cancel: true,
          },
        ];
        this.editingMode = 'add';
        this.$root.$emit('drawSidebarGeometry', {
          drawendCallback: e => {
            const geom = this.prepareFeatureGeometry(e.feature, type);
            this.drawingGeometry = geom;
            this.handleModifyGeometry(type);
          },
          geometryType: type,
        });
      }, 0);
    },
    prepareFeatureGeometry(feature, type) {
      const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
      const multi = new { MultiPoint, MultiPolygon }[`Multi${capitalizedType}`]([]);
      multi[`append${capitalizedType}`](feature.getGeometry());
      const t = new GeoJSON().writeGeometryObject(multi, {
        dataProjection: this.$_config.defaultEpsg,
        featureProjection: this.$_config.defaultEpsg,
      });
      return {
        ...t,
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg,
          },
        },
      };
    },
  },
};

const utils = {
  computed: {
    token: get('authentication/token'),
  },
  methods: {
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getImageSource(imgSrc) {
      const isRelative = imgSrc?.startsWith('/');
      const imgUrl = isRelative ? `${process.env.VUE_APP_API_URL}${imgSrc}?token=${this.token}` : imgSrc;
      return imgUrl ?? this.getImagePlaceholder();
    },
    getImagePlaceholder() {
      return imagePlaceholder;
    },
    getDocumentTitle(prependText, properties) {
      return `${prependText ? `${prependText} - ` : ''}${this.$i18n.t('table.rziDocuments.documentation', {
        code: properties[documentation.documents.attributes.docCode],
      })}${
        properties[documentation.documents.attributes.docType] === 3
          ? ` - ${properties[documentation.documents.attributes.docTitle]}`
          : ''
      }`;
    },
    async getSingleDatasourceFeatures(dataSource, type) {
      const matchedTables = Object.keys(this.currentMetadata.tableViews).filter(key =>
        Object.values(this.currentMetadata.tableViews[key].datasources).find(dt => dt === dataSource)
      );
      const filterAttribute = this.currentMetadata.tableViewsSharedData.filterAttribute;
      const r = await this.getDataSourceFeatures({
        dataSource,
        filters: {
          $EQUAL: {
            [`${dataSource}.${filterAttribute}`]: { value: this.item.id },
          },
        },
      });
      return matchedTables.map(table => [
        table,
        r.features
          .filter(ft =>
            (this.currentMetadata.tableViews[table]?.statics || []).every(
              dt => ft.properties[dt.attribute] === dt.value
            )
          )
          .map(ft => ({
            feature: ft,
            datasource: dataSource,
            type: type,
          })),
      ]);
    },
    async handleRefreshTableDatasources(datasourcesToRefresh, callback) {
      const r = await Promise.all(
        datasourcesToRefresh.map(item => this.getSingleDatasourceFeatures(item.name, item.type))
      );
      const mergedArray = r.flat().reduce((acc, item) => {
        const existingItem = acc.find(i => i[0] === item[0]);
        existingItem ? (existingItem[1] = [...existingItem[1], ...item[1]]) : acc.push(item);
        return acc;
      }, []);
      const cardTablesCopy = JSON.parse(JSON.stringify(this.cardTables));
      this.cardTables = mergedArray.reduce((acc, item) => {
        acc[item[0]] = [
          ...acc[item[0]].filter(itm => !datasourcesToRefresh.map(dt => dt.name).includes(itm.datasource)),
          ...item[1],
        ];
        return acc;
      }, cardTablesCopy);
      callback();
    },
    async retrieveScores(type, endCallback, nonTypeHandler) {
      const mainItem = this.currentMetadata.type === 'Monitoring' ? this.mainItem : this.item;
      if (type) {
        const data = await this.getScorePromise(mainItem, type, this.scores.rawParameters, this.scores.scoreParameters);
        this.scores = {
          ...this.scores,
          [type]: data.features,
        };
      } else {
        const [rawParameters, scoreParameters] = await Promise.all([
          this.getScorePromise(mainItem, 'rawParameters'),
          this.getScorePromise(mainItem, 'scoreParameters'),
        ]);
        const [rawIndexes, scoreIndexes] = await Promise.all([
          this.getScorePromise(mainItem, 'rawIndexes', rawParameters.features, scoreParameters.features),
          this.getScorePromise(mainItem, 'scoreIndexes', rawParameters.features, scoreParameters.features),
        ]);
        if (nonTypeHandler)
          nonTypeHandler({
            rawIndexes,
            rawParameters,
            scoreParameters,
            scoreIndexes,
          });
        this.scores = {
          rawParameters: rawParameters.features,
          rawIndexes: rawIndexes.features.length ? rawIndexes.features : null,
          scoreParameters: scoreParameters.features,
          scoreIndexes: scoreIndexes.features,
        };
      }
      if (endCallback) endCallback(this.computedScoresData);
    },
    getScorePromise(mainItem, type, rawParameters, scoreParameters) {
      switch (type) {
        case 'rawParameters': {
          const subType = this.sitesTypesFeatures.find(
            siteType =>
              siteType.properties[sitesTypes.metadata.attributes.name] ===
              mainItem.feature.properties[this.currentMetadata.protectedSites.attributes.typeName]
          );
          return this.getDataSourceFeatures({
            dataSource: this.currentMetadata.score.dictRawParameters.datasource,
            filters: {
              $EQUAL: {
                [`${this.currentMetadata.score.dictRawParameters.datasource}.${this.currentMetadata.score.dictRawParameters.attributes.type}`]: {
                  value: this.sitesTypesFeatures.find(
                    siteType =>
                      siteType.properties[sitesTypes.metadata.attributes.code] === subType.properties.custom_mainKey
                  )?.id,
                },
              },
            },
          });
        }
        case 'scoreParameters':
          return this.getDataSourceFeatures({
            dataSource: this.currentMetadata.score.parameters.datasource,
            filters: {
              $EQUAL: {
                [`${this.currentMetadata.score.parameters.datasource}.${this.currentMetadata.score.parameters.filterAttribute}`]: {
                  value: this.item.id,
                },
              },
            },
          });
        case 'rawIndexes':
          return this.getDataSourceFeatures({
            dataSource: this.currentMetadata.score.dictRawIndexes.datasource,
            filters: {
              '!AND': [
                {
                  $EQUAL: {
                    [`${this.currentMetadata.score.dictRawIndexes.datasource}.${this.currentMetadata.score.dictRawIndexes.attributes.protectedSite}`]: {
                      value:
                        mainItem.feature.properties[
                          this.currentMetadata.protectedSites.attributes[
                            this.currentMetadata.type === 'Monitoring' ? 'subtitle' : 'title'
                          ]
                        ],
                    },
                  },
                },
                {
                  $IN: {
                    [`${this.currentMetadata.score.dictRawIndexes.datasource}.${this.currentMetadata.score.dictRawIndexes.attributes.parameter}`]: rawParameters.map(
                      param => param.id
                    ),
                  },
                },
              ],
            },
          });
        case 'scoreIndexes':
          return this.getDataSourceFeatures({
            dataSource: this.currentMetadata.score.indexes.datasource,
            filters:
              this.currentMetadata.type === 'Monitoring'
                ? {
                    $EQUAL: {
                      [`${this.currentMetadata.score.indexes.datasource}.${this.currentMetadata.score.indexes.attributes.bindedParent}`]: {
                        value: this.item.id,
                      },
                    },
                  }
                : {
                    $IN: {
                      [`${this.currentMetadata.score.indexes.datasource}.${this.currentMetadata.score.indexes.attributes.bindedParameter}`]: scoreParameters.map(
                        ft => ft.id
                      ),
                    },
                  },
          });
      }
    },
  },
};

export { drawing, utils };
